import { useDispatch } from 'react-redux';
import { useMutation } from '@tanstack/react-query';
import ObrasApi from 'Data/Obras.api';
import { FILES_QUERY_KEY } from 'Data/keys/QueryKeys';
import { IDeleteFilesRequest } from 'Data/interfaces/Obra/IDeleteFilesRequest';
import { IUseQueryFilesReturn } from 'Data/interfaces/ListagemArquivos/IListFiles';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { ListagemArquivosActions } from 'Store/ListagemArquivos/ListagemArquivos.actions';
import { queryClient } from 'Context/QueryClientContext';
import toastHandler from 'Utils/toastHandler';

interface IUseQueryDeleteFilesProps {
  onClearSelectedFiles: () => void;
}

export const useQueryDeleteFiles = ({
  onClearSelectedFiles,
}: IUseQueryDeleteFilesProps) => {
  const dispatch = useDispatch();

  const { mutate, isPending } = useMutation({
    mutationFn: (variables: IDeleteFilesRequest) => {
      return ObrasApi.deleteFiles(variables);
    },
    onMutate: async (variables) => {
      const previousFiles = queryClient.getQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.FolderId],
      );

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.FolderId],
        (old) => {
          if (old) {
            for (const fileId of variables.FilesId) {
              const index = old.ListFiles.findIndex((file) => {
                return file.Identifier.ApiId === fileId;
              });
              if (index !== -1) {
                if (
                  variables.RestoreObsoleteFiles &&
                  variables.RestoreObsoleteFiles?.length > 0 &&
                  old.ListFiles[index]?.Versions &&
                  (old.ListFiles[index]?.Versions?.length || 0) > 0
                ) {
                  const lastFile = (old.ListFiles[index].Versions as IFileData[]).splice(0, 1)[0];
                  if (lastFile) {
                    lastFile.Versions = (old.ListFiles[index].Versions as IFileData[]);
                    old.ListFiles[index] = lastFile;
                  }
                } else {
                  old.ListFiles.splice(index, 1);
                }
              }
            }
            return { ...old };
          }
          return old;
        },
      );
      dispatch(ListagemArquivosActions.setShowModalDeleteFiles(false));
      onClearSelectedFiles();

      return { previousFiles };
    },
    onSuccess: async (_data, variables) => {
      let coreMsg = 'Arquivo(s) excluído(s)';
      if (variables.hasFolder) {
        if (variables.onlyFolder) {
          coreMsg = 'Pasta(s) excluída(s)';
        } else {
          coreMsg = 'Arquivo(s) e Pasta(s) excluído(s)';
        }
      }
      const msg = `${coreMsg} com sucesso!`;
      toastHandler.showSuccess(msg);
    },
    onError: async (error: any, variables, context) => {
      await queryClient.cancelQueries({
        queryKey: [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.FolderId],
      });

      queryClient.setQueryData<IUseQueryFilesReturn>(
        [FILES_QUERY_KEY, Number(variables.ConstructionSiteId), variables.FolderId],
        context?.previousFiles,
      );

      const errorMsg = error.errors
        ? error.errors[0].Message
        : 'Erro ao realizar exclusão dos arquivos';
      toastHandler.showError(errorMsg);
      dispatch(ListagemArquivosActions.setShowModalDeleteFiles(false));
    },
  });

  return {
    deleteFiles: mutate,
    isLoading: isPending,
  };
};
